import { isValidPhoneNumber, type CountryCode } from 'libphonenumber-js';
import { parsePhoneNumberWithError } from 'libphonenumber-js/max';

export default class PhoneNumber {
  /**
   * Formats a phone number for display.
   * - For French numbers, it returns the national format (e.g., 01 23 45 67 89).
   * - For other countries, it returns the international format (e.g., +32 470 12 34 56).
   * All spaces are replaced with non-breaking spaces.
   *
   * @param phoneNumber - The phone number to format (expects E.164 format).
   * @returns The formatted phone number string.
   * @throws {Error} If the phone number is invalid.
   */
  public static format = (phoneNumber: string): string => {
    const parsedNumber = parsePhoneNumberWithError(phoneNumber);
    const format =
      parsedNumber.country === 'FR'
        ? parsedNumber.formatNational()
        : parsedNumber.formatInternational();

    return format.replace(/ /g, '\u00A0'); // Replace space with non-breaking space
  };

  /**
   * Returns multiple formats of a phone number
   *
   * @param phoneNumber - The phone number to format (expects E.164 format).
   * @returns An array of formatted phone number strings.
   * @throws {Error} If the phone number is invalid.
   */
  public static formatAll = (phoneNumber: string): string[] => {
    const formatted = PhoneNumber.format(phoneNumber);

    if (formatted === phoneNumber) {
      // Non-French number, return only E.164
      return [phoneNumber];
    }

    return [phoneNumber, formatted, formatted.replace(/\u00A0/g, '')];
  };

  /**
   * Converts a phone number to the E.164 standard format.
   *
   * @param phoneNumber - The phone number to convert.
   * @param defaultCountry - The default country to assume if the number is not in international format.
   * @returns The phone number in E.164 format (e.g., +33123456789).
   * @throws {Error} If the phone number is invalid.
   */
  public static toE164 = (
    phoneNumber: string,
    defaultCountry: CountryCode = 'FR',
  ): string => {
    const parsedNumber = parsePhoneNumberWithError(phoneNumber, defaultCountry);
    return parsedNumber.format('E.164');
  };

  /**
   * Checks if a phone number is valid.
   * This function supports international formats for any country, and assumes
   * the French national format for numbers without a country code.
   *
   * @param phoneNumber - The phone number string to validate.
   * @returns True if the phone number is valid, false otherwise.
   */
  public static isValid = (phoneNumber?: string): boolean => {
    if (!phoneNumber) return false;
    return isValidPhoneNumber(phoneNumber, 'FR');
  };

  /**
   * Check if a phone number can receive SMS messages.
   *
   * @param phoneNumber - The phone number to check (expects E.164 format).
   * @returns True if the phone number is SMS capable, false otherwise.
   * @throws {Error} If the phone number is invalid.
   */
  public static isSmsCapable = (phoneNumber: string): boolean => {
    const number = parsePhoneNumberWithError(phoneNumber);
    const numberType = number.getType();
    return numberType === 'MOBILE' || numberType === 'FIXED_LINE_OR_MOBILE';
  };
}
